'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import Breadcrumb from '@/components/Breadcrumb';

const supabase = createClient();

interface PdfConfig {
  id: string;
  logo_url: string | null;
  colore_primario: string;
  colore_secondario: string;
  intestazione: string;
  piede_pagina: string;
  firma_responsabile: string;
}

export default function PdfReportContent() {
  const [config, setConfig] = useState<PdfConfig | null>(null);
  const [form, setForm] = useState({ logo_url: '', colore_primario: '#0ea5e9', colore_secondario: '#0f172a', intestazione: '', piede_pagina: '', firma_responsabile: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [reportType, setReportType] = useState('kpi');
  const [saved, setSaved] = useState(false);
  const { profile } = useAuth();

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('pdf_report_config').select('*').limit(1).single();
    if (data) {
      setConfig(data);
      setForm({
        logo_url: data.logo_url || '',
        colore_primario: data.colore_primario || '#0ea5e9',
        colore_secondario: data.colore_secondario || '#0f172a',
        intestazione: data.intestazione || '',
        piede_pagina: data.piede_pagina || '',
        firma_responsabile: data.firma_responsabile || '',
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveConfig = async () => {
    setSaving(true);
    if (config) {
      await supabase.from('pdf_report_config').update({ ...form, updated_at: new Date().toISOString() }).eq('id', config.id);
    } else {
      await supabase.from('pdf_report_config').insert(form);
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    setSaving(false);
    fetchData();
  };

  const generatePdf = async () => {
    setGenerating(true);
    try {
      // Fetch data based on report type
      let reportData: any = {};
      if (reportType === 'kpi') {
        const { data: kpi } = await supabase.from('kpi_snapshot').select('*').order('data_snapshot', { ascending: false }).limit(1);
        const { data: devices } = await supabase.from('devices').select('stato').limit(1000);
        reportData = { kpi: kpi?.[0], devices };
      } else if (reportType === 'ticket') {
        const { data: tickets } = await supabase.from('tickets').select('*').order('created_at', { ascending: false }).limit(50);
        reportData = { tickets };
      } else if (reportType === 'inventario') {
        const { data: devices } = await supabase.from('devices').select('*').order('nome').limit(100);
        reportData = { devices };
      }

      // Dynamic import jsPDF
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      const primaryColor = form.colore_primario || '#0ea5e9';
      const r = parseInt(primaryColor.slice(1, 3), 16);
      const g = parseInt(primaryColor.slice(3, 5), 16);
      const b = parseInt(primaryColor.slice(5, 7), 16);

      // Header background
      doc.setFillColor(r, g, b);
      doc.rect(0, 0, 210, 35, 'F');

      // Logo placeholder or text
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text(form.intestazione || 'GestioneLab', 15, 20);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Report ${reportType.toUpperCase()} · ${new Date().toLocaleDateString('it-IT')}`, 15, 28);

      // Content
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`Report ${reportType.charAt(0).toUpperCase() + reportType.slice(1)}`, 15, 50);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      let yPos = 60;

      if (reportType === 'kpi' && reportData.kpi) {
        const kpi = reportData.kpi;
        const rows = [
          ['Dispositivi Totali', String(kpi.totale_dispositivi || 0)],
          ['Dispositivi Online', String(kpi.dispositivi_online || 0)],
          ['Ticket Aperti', String(kpi.ticket_aperti || 0)],
          ['SLA Rispettati', `${kpi.sla_rispettati_percentuale || 0}%`],
          ['Uptime Medio', `${kpi.uptime_medio_percentuale || 0}%`],
          ['Costo Manutenzione Mese', `€${(kpi.costo_manutenzione_mese || 0).toLocaleString('it-IT')}`],
        ];
        rows.forEach(([label, value]) => {
          doc.setFont('helvetica', 'bold');
          doc.text(label + ':', 15, yPos);
          doc.setFont('helvetica', 'normal');
          doc.text(value, 80, yPos);
          yPos += 8;
        });
      } else if (reportType === 'ticket' && reportData.tickets) {
        const tickets = reportData.tickets.slice(0, 20);
        doc.text('Ultimi 20 Ticket:', 15, yPos);
        yPos += 8;
        tickets.forEach((t: any) => {
          doc.text(`• ${t.numero_ticket} - ${t.titolo?.slice(0, 50) || 'N/A'} [${t.stato}]`, 15, yPos);
          yPos += 6;
          if (yPos > 270) { doc.addPage(); yPos = 20; }
        });
      } else if (reportType === 'inventario' && reportData.devices) {
        const devices = reportData.devices.slice(0, 30);
        doc.text('Inventario Dispositivi:', 15, yPos);
        yPos += 8;
        devices.forEach((d: any) => {
          doc.text(`• ${d.nome || 'N/A'} (${d.tipo || 'N/A'}) - ${d.stato || 'N/A'}`, 15, yPos);
          yPos += 6;
          if (yPos > 270) { doc.addPage(); yPos = 20; }
        });
      }

      // Footer
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFillColor(r, g, b);
        doc.rect(0, 285, 210, 12, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.text(form.piede_pagina || 'GestioneLab · gestionelab.com', 15, 292);
        doc.text(`Pagina ${i} di ${pageCount}`, 180, 292);
        if (form.firma_responsabile) {
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(9);
          doc.text(`Responsabile: ${form.firma_responsabile}`, 15, yPos + 20);
        }
      }

      doc.save(`report-${reportType}-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (e) {
      console.error('PDF generation error:', e);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: 'Report PDF con Logo' }]} />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Report PDF con Logo Struttura</h1>
          <p className="text-text-secondary text-sm mt-1">Configura il branding e genera report PDF personalizzati</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Config */}
        <div className="bg-surface-elevated border border-border rounded-xl p-5">
          <h3 className="font-semibold text-white mb-4">Configurazione Branding</h3>
          {loading ? (
            <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" /></div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-muted-color mb-1">URL Logo Struttura</label>
                <input value={form.logo_url} onChange={e => setForm(p => ({ ...p, logo_url: e.target.value }))}
                  className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-white"
                  placeholder="https://..." />
                {form.logo_url && (
                  <img src={form.logo_url} alt="Logo preview" className="mt-2 h-12 object-contain" onError={e => (e.currentTarget.style.display = 'none')} />
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-muted-color mb-1">Colore Primario</label>
                  <div className="flex gap-2">
                    <input type="color" value={form.colore_primario} onChange={e => setForm(p => ({ ...p, colore_primario: e.target.value }))}
                      className="w-10 h-10 rounded-lg border border-border cursor-pointer" />
                    <input value={form.colore_primario} onChange={e => setForm(p => ({ ...p, colore_primario: e.target.value }))}
                      className="flex-1 bg-surface border border-border rounded-lg px-3 py-2 text-sm text-white font-mono" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-muted-color mb-1">Colore Secondario</label>
                  <div className="flex gap-2">
                    <input type="color" value={form.colore_secondario} onChange={e => setForm(p => ({ ...p, colore_secondario: e.target.value }))}
                      className="w-10 h-10 rounded-lg border border-border cursor-pointer" />
                    <input value={form.colore_secondario} onChange={e => setForm(p => ({ ...p, colore_secondario: e.target.value }))}
                      className="flex-1 bg-surface border border-border rounded-lg px-3 py-2 text-sm text-white font-mono" />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm text-muted-color mb-1">Intestazione Report</label>
                <input value={form.intestazione} onChange={e => setForm(p => ({ ...p, intestazione: e.target.value }))}
                  className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-white"
                  placeholder="Es. Ospedale San Giovanni - Reparto IT" />
              </div>
              <div>
                <label className="block text-sm text-muted-color mb-1">Piede Pagina</label>
                <input value={form.piede_pagina} onChange={e => setForm(p => ({ ...p, piede_pagina: e.target.value }))}
                  className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-white"
                  placeholder="Es. Documento riservato · Ospedale San Giovanni" />
              </div>
              <div>
                <label className="block text-sm text-muted-color mb-1">Firma Responsabile</label>
                <input value={form.firma_responsabile} onChange={e => setForm(p => ({ ...p, firma_responsabile: e.target.value }))}
                  className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-white"
                  placeholder="Es. Dott. Mario Rossi - Responsabile IT" />
              </div>
              {saved && (
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm flex items-center gap-2">
                  <Icon name="CheckCircleIcon" size={16} /> Configurazione salvata!
                </div>
              )}
              <button onClick={saveConfig} disabled={saving}
                className="w-full bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white py-2 rounded-lg text-sm font-semibold transition-colors">
                {saving ? 'Salvataggio...' : 'Salva Configurazione'}
              </button>
            </div>
          )}
        </div>

        {/* Generator */}
        <div className="bg-surface-elevated border border-border rounded-xl p-5">
          <h3 className="font-semibold text-white mb-4">Genera Report PDF</h3>

          {/* Preview */}
          <div className="mb-4 rounded-xl overflow-hidden border border-border">
            <div className="p-4 text-white" style={{ backgroundColor: form.colore_primario }}>
              <p className="font-bold text-lg">{form.intestazione || 'GestioneLab'}</p>
              <p className="text-sm opacity-80">Report · {new Date().toLocaleDateString('it-IT')}</p>
            </div>
            <div className="p-4 bg-white">
              <p className="text-gray-800 text-sm font-semibold mb-2">Anteprima contenuto report</p>
              <div className="space-y-1">
                {['Dispositivi Totali: --', 'Ticket Aperti: --', 'SLA Rispettati: --%'].map(r => (
                  <p key={r} className="text-xs text-gray-500">{r}</p>
                ))}
              </div>
            </div>
            <div className="p-2 text-white text-xs" style={{ backgroundColor: form.colore_primario }}>
              {form.piede_pagina || 'GestioneLab · gestionelab.com'}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-muted-color mb-1">Tipo Report</label>
              <select value={reportType} onChange={e => setReportType(e.target.value)}
                className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-white">
                <option value="kpi">KPI e Performance</option>
                <option value="ticket">Gestione Ticket</option>
                <option value="inventario">Inventario Dispositivi</option>
              </select>
            </div>
            <button onClick={generatePdf} disabled={generating}
              className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white py-3 rounded-lg text-sm font-semibold transition-colors">
              <Icon name={generating ? 'ArrowPathIcon' : 'DocumentArrowDownIcon'} size={16} className={generating ? 'animate-spin' : ''} />
              {generating ? 'Generazione PDF...' : 'Genera e Scarica PDF'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
